import { DocumentArt } from '@idc-forms/client-app-domain/documents/document-art'

/**
 * Las miniaturas DIBUJADAS de los documentos — el truco central de la maqueta.
 *
 * Nada de esto es una vista previa: son composiciones que se leen como «una hoja de texto», «un
 * plano», «una foto». La maqueta original las hacía con CSS; la app las rehízo con
 * `react-native-svg` porque allá no hay degradados; acá **vuelven a ser CSS**, que es de donde
 * salieron. Los valores —el ancho desparejo de los renglones, la retícula cada 9 px, el paisaje del
 * marco— son los de `apps/mobile/src/features/documentos/ui/document-art.tsx`, línea por línea.
 *
 * Qué arte le toca a cada documento lo decide `documentArt()` del paquete de dominio, no esto: acá
 * sólo se dibuja.
 *
 * El papel NO sigue al tema (`--cap-art-*` en `theme/cap-preview.css`): una hoja es blanca aunque
 * el teléfono esté en oscuro.
 */

/** Hoja de texto: renglones, un recuadro de firma, el cierre. */
function ArtePdf() {
  return (
    <div
      className="flex h-full w-full flex-col rounded-[3px] p-2"
      style={{ backgroundColor: 'var(--cap-art-sheet)', gap: 5 }}
    >
      <div
        style={{ height: 4, width: '72%', borderRadius: 2, background: 'var(--cap-art-headline)' }}
      />
      <div
        style={{ height: 3, width: '100%', borderRadius: 2, background: 'var(--cap-art-line)' }}
      />
      <div
        style={{ height: 3, width: '94%', borderRadius: 2, background: 'var(--cap-art-line)' }}
      />
      <div
        style={{ height: 3, width: '96%', borderRadius: 2, background: 'var(--cap-art-line)' }}
      />
      <div
        style={{
          marginTop: 2,
          height: 20,
          borderRadius: 2,
          border: '1px solid var(--cap-art-box)'
        }}
      />
      <div
        style={{ height: 3, width: '40%', borderRadius: 2, background: 'var(--cap-art-line)' }}
      />
    </div>
  )
}

/** Plano: la retícula de papel milimetrado dentro de un marco. */
function ArtePlano() {
  return (
    <div
      className="h-full w-full rounded-[3px] p-1.5"
      style={{ backgroundColor: 'var(--cap-art-sheet)' }}
    >
      <div
        className="cap-art-mesh h-full w-full"
        style={{ border: '1px solid var(--cap-art-mesh-frame)' }}
      />
    </div>
  )
}

/** Fotografía: el paisaje esquemático —sol y montañas— en los azules del papel milimetrado. */
function ArteImagen() {
  return (
    <div
      className="h-full w-full rounded-[3px] p-1.5"
      style={{ backgroundColor: 'var(--cap-art-sheet)' }}
    >
      <div
        className="h-full w-full overflow-hidden"
        style={{ border: '1px solid var(--cap-art-mesh-frame)' }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 36 46"
          preserveAspectRatio="xMidYMid slice"
          role="presentation"
        >
          <circle cx={26} cy={12} r={4.5} fill="var(--cap-art-mesh)" />
          <path
            d="M-2 40 L12 22 L20 31 L27 24 L38 38 L38 48 L-2 48 Z"
            fill="var(--cap-art-mesh)"
            stroke="var(--cap-art-mesh-frame)"
            strokeWidth={1}
          />
        </svg>
      </div>
    </div>
  )
}

/** El despachador: la tarjeta y la ficha piden el arte por su clave, no con un `switch` cada una. */
export function CapDocumentArt({ art }: { art: DocumentArt }) {
  if (art === DocumentArt.plano) return <ArtePlano />
  if (art === DocumentArt.imagen) return <ArteImagen />
  return <ArtePdf />
}
